import { editorInstance } from "@/code-editor";
import AiIcon from "@/components/ai-icon";
import styles from "@/components/corner-actions.module.css";
import { handleSaveClick, isFileOpened } from "@/file";
import globals from "@/globals";
import * as signals from "@/signals";
import {
	closeAiBar,
	getPremium,
	handleKeyboardClick,
	showDialog,
} from "@/utils";
import {
	faAngleLeft,
	faAngleRight,
	faBars,
	faSave,
	faTags,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, useState } from "react";

function CornerActions(): JSX.Element {
	const [isFolded, setIsFolded] = useState(false);

	const showMainMenu = (): void => {
		signals.isMainMenuShown.value = true;
	};

	const toggleAiBar = (): void => {
		if (signals.isClosingSidebar.value || !isFileOpened()) {
			return;
		}
		if (signals.isAiBarShown.value) {
			void closeAiBar();
			return;
		}
		if (!signals.login.value.username) {
			void showDialog(t("notLoggedIn"));
			return;
		}
		if (
			typeof signals.files.value[signals.currentFile.value] !== "string"
		) {
			void showDialog(t("onlyCodeFilesSupported"));
			return;
		}
		signals.isAiBarShown.value = true;
		document.documentElement.classList.add("has-sidebar");
	};

	const toggleFold = (): void => {
		if (isFolded) {
			document.documentElement.classList.remove("fullscreen");
		} else {
			document.documentElement.classList.add("fullscreen");
		}
		setIsFolded(!isFolded);
		window.setTimeout(() => {
			editorInstance?.layout();
		}, 250);
	};

	return (
		<>
			<div className={styles["actions-container"]}>
				{signals.dynamicInfo.value.promotionText && (
					<button
						className={styles["promotional-text"]}
						onClick={() => void getPremium()}
						type="button"
					>
						<FontAwesomeIcon icon={faTags} />
						{signals.dynamicInfo.value.promotionText}
					</button>
				)}
				{(signals.hasPremium.value || !globals.isTencent) && (
					<div
						onClick={toggleAiBar}
						onKeyDown={handleKeyboardClick(toggleAiBar)}
						role="button"
						tabIndex={0}
						title={t("aiProgrammer")}
					>
						<AiIcon className={styles["ai-icon"]} />
					</div>
				)}
				<div
					className={clsx(
						"hide-on-mobile",
						signals.hasUnsaved.value && "highlight",
					)}
					onClick={handleSaveClick}
					onKeyDown={handleKeyboardClick(handleSaveClick)}
					role="button"
					tabIndex={0}
					title={t("save")}
				>
					<FontAwesomeIcon icon={faSave} />
				</div>
				<div
					aria-haspopup="menu"
					onClick={showMainMenu}
					onKeyDown={handleKeyboardClick(showMainMenu)}
					role="button"
					tabIndex={0}
					title={t("menu")}
				>
					<FontAwesomeIcon icon={faBars} />
				</div>
			</div>
			<div
				aria-expanded={!isFolded}
				className={clsx(styles["float-button"], "hide-on-mobile")}
				onClick={toggleFold}
				onKeyDown={handleKeyboardClick(toggleFold)}
				role="button"
				tabIndex={0}
				title={isFolded ? t("expand") : t("fold")}
			>
				<FontAwesomeIcon icon={isFolded ? faAngleRight : faAngleLeft} />
			</div>
		</>
	);
}

export default CornerActions;
